import { ErrorResponse, IRestApiService } from '../types';
import { TokenStorage } from './TokenStorage';
import { handlePlatformSuspended } from './PlatformSuspendedScreen';
import { isBackendBack, isBackendUnavailable, waitForBackend } from './BackendUnavailableScreen';

type MiddlewareFunction = (
  service: RestApiService
) => (next: (response: Response) => Promise<any>) => (response: Response) => Promise<any>;

type TimeoutMiddlewareFunction = (service: RestApiService) => (next: (response: any) => any) => (response: any) => any;

interface RequestOptions {
  responseType?: 'json' | 'blob' | 'text' | 'arrayBuffer';
  cache?: RequestCache;
}

class RestApiService implements IRestApiService {
  private baseUrl: string;
  private middleware: (next: (response: Response) => Promise<any>) => (response: Response) => Promise<any>;
  private timeoutMiddleware: (next: (response: any) => any) => any;
  private _refreshPromise: Promise<boolean> | null = null;
  private _onAuthFailure: (() => void) | null = null;

  constructor(
    baseUrl: string,
    middleware?: MiddlewareFunction,
    timeoutMiddleware?: TimeoutMiddlewareFunction,
    onAuthFailure?: () => void
  ) {
    this.baseUrl = baseUrl;
    this.middleware = middleware ? middleware(this) : (next) => (response) => next(response);
    this.timeoutMiddleware = timeoutMiddleware
      ? timeoutMiddleware(this)
      : () => {
          console.log('backend timeout');
        };
    this._onAuthFailure = onAuthFailure ?? null;
  }

  get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.doRequest<T>(url, 'GET', undefined, 40000, options);
  }

  post<T>(url: string, body?: any, timeout?: number): Promise<T> {
    return this.doRequest<T>(url, 'POST', body, timeout);
  }

  postAs<T>(url: string, token: string, body?: any): Promise<T> {
    const vooptyLang = localStorage.getItem('voopty-lang') ?? 'en_US';
    return fetch(this._getApiUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        vooptyauthorization: `Bearer ${token}`,
        'voopty-lang': vooptyLang,
      },
      body: JSON.stringify(body || {}),
      credentials: 'include',
    }).then(this.middleware(this._handleResponse.bind(this))) as Promise<T>;
  }

  delete<T>(url: string, body?: any): Promise<T> {
    return this.doRequest<T>(url, 'DELETE', body);
  }

  put<T>(url: string, body?: any): Promise<T> {
    return this.doRequest<T>(url, 'PUT', body);
  }

  patch<T>(url: string, body?: any): Promise<T> {
    return this.doRequest<T>(url, 'PATCH', body);
  }

  file<T>(url: string, file: File): Promise<T> {
    const header = this._createRequestHeader();
    const formData = new FormData();
    formData.append('file', file);

    return fetch(this._getApiUrl(url), {
      method: 'POST',
      body: formData,
      headers: header,
      credentials: 'include',
    }).then(this.middleware(this._handleResponse));
  }

  public async doRequest<T>(
    url: string,
    httpMethod: string,
    body?: any,
    timeout = 40000,
    options?: RequestOptions,
    _isRetry = false
  ): Promise<T> {
    const header = this._createRequestHeader();
    let requestBody: string | null = null;

    if (httpMethod !== 'GET') {
      header['Content-Type'] = 'application/json; charset=utf-8';
      requestBody = JSON.stringify(body || {});
    }

    const responseHandler = options?.responseType === 'blob' ? this._handleBlobResponse : this._handleResponse;

    let rawResponse: Response;
    try {
      rawResponse = await Promise.race<Response>([
        fetch(this._getApiUrl(url), {
          method: httpMethod,
          headers: header,
          body: requestBody,
          credentials: 'include',
          cache: options?.cache,
        }),
        new Promise<Response>((_, reject) => setTimeout(() => reject(new Error('backend-timeout')), timeout)),
      ]);
    } catch (e: any) {
      if (e.message === 'backend-timeout') {
        this.timeoutMiddleware(this.middleware(this._handleBackendTimeout));
        return undefined as unknown as T;
      }
      throw e;
    }

    if (rawResponse.status === 401 && !_isRetry) {
      let refreshed: boolean;
      try {
        refreshed = await this._tryRefreshToken();
      } catch {
        // Network or server error during refresh — do NOT logout, propagate as service error
        throw { status: 503, statusText: 'Service temporarily unavailable' } as ErrorResponse;
      }
      if (refreshed) {
        return this.doRequest<T>(url, httpMethod, body, timeout, options, true);
      }
      // refreshed === false means the refresh token is genuinely invalid → trigger logout
      this._onAuthFailure?.();
      throw { status: 401, statusText: 'Unauthorized' } as ErrorResponse;
    }

    // Backend being redeployed (VOOP-2473): cover the app with the VOOPTY loader until it's back.
    // Only GETs are repeated automatically - a 502 can also mean the backend died mid-request, so a
    // mutation may already have been applied; after the wait it fails as before and the user retries.
    if (await this._isBackendUnavailable(rawResponse)) {
      await waitForBackend(() => isBackendBack(this.baseUrl));
      if (httpMethod === 'GET') {
        return this.doRequest<T>(url, httpMethod, body, timeout, options, _isRetry);
      }
    }

    return this.middleware(responseHandler.bind(this))(rawResponse) as Promise<T>;
  }

  private async _isBackendUnavailable(response: Response): Promise<boolean> {
    if (response.status !== 502 && response.status !== 503) return false;
    // clone(): the original body is still read by the normal response handler afterwards.
    const body = await response
      .clone()
      .json()
      .catch(() => undefined);
    return isBackendUnavailable(response.status, body);
  }

  private _tryRefreshToken(): Promise<boolean> {
    if (this._refreshPromise) return this._refreshPromise;
    this._refreshPromise = this._doRefresh().finally(() => {
      this._refreshPromise = null;
    });
    return this._refreshPromise;
  }

  private async _doRefresh(): Promise<boolean> {
    const refreshToken = TokenStorage.getRefreshToken();
    const refreshEndpoint = TokenStorage.getRefreshEndpoint();
    if (!refreshToken || !refreshEndpoint) return false;

    let response: Response;
    try {
      response = await fetch(this._getApiUrl(refreshEndpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // Network error — transient, should NOT cause logout
      throw new Error('refresh_network_error');
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Another tab may have already rotated this token — check if refresh token changed
        const currentRefreshToken = TokenStorage.getRefreshToken();
        if (currentRefreshToken && currentRefreshToken !== refreshToken) {
          // Another tab refreshed; access token is already updated in storage
          return true;
        }
        return false; // Token is genuinely invalid/expired → logout is correct
      }
      // Server error (5xx) — transient, should NOT cause logout
      throw new Error(`refresh_server_error_${response.status}`);
    }

    const data = await response.json();
    if (data.token) {
      TokenStorage.updateTokens(data.token, data.refreshToken ?? null);
      return true;
    }
    return false;
  }

  private _handleBackendTimeout(response: any): never {
    throw { statusText: response, status: 504 } as ErrorResponse;
  }

  private _handleBlobResponse(response: Response): Promise<Blob> {
    if (response.ok) {
      return response.blob();
    }

    return response.json().then(
      (responseJson) => {
        handlePlatformSuspended(response.status, responseJson);
        throw {
          statusText: response.statusText,
          status: response.status,
          errorCode: responseJson.errorCode,
        } as ErrorResponse;
      },
      () => {
        throw {
          statusText: response.statusText,
          status: response.status,
        } as ErrorResponse;
      }
    );
  }

  private _handleResponse(response: Response): Promise<any> {
    if (response.ok) {
      return response.json();
    }
    if (response.status === 400) {
      return response.json().then(
        (responseJson) => {
          if (Array.isArray(responseJson) && responseJson[0]?.validationError) {
            throw responseJson;
          }
          responseJson.validation = true;
          throw {
            statusText: response.statusText,
            status: response.status,
            errorCode: responseJson.errorCode,
            data: responseJson,
          } as ErrorResponse;
        },
        () => {
          throw {
            statusText: response.statusText,
            status: response.status,
          } as ErrorResponse;
        }
      );
    }
    return response.json().then(
      (responseJson) => {
        handlePlatformSuspended(response.status, responseJson);
        throw {
          statusText: response.statusText,
          status: response.status,
          errorCode: responseJson.errorCode,
          data: responseJson,
        } as ErrorResponse;
      },
      () => {
        throw {
          statusText: response.statusText,
          status: response.status,
        } as ErrorResponse;
      }
    );
  }

  private _createRequestHeader(): Record<string, string> {
    const header: Record<string, string> = {};
    try {
      const token = TokenStorage.getToken();
      const vooptyLang = localStorage.getItem('voopty-lang');
      const utm = localStorage.getItem('utm');

      if (utm) header['utm'] = utm;

      if (token) {
        return {
          ...header,
          vooptyauthorization: `Bearer ${token}`,
          'voopty-lang': vooptyLang ?? 'en_US',
        };
      }
    } catch (e) {
      console.error(e);
    }

    return header;
  }

  private _getApiUrl(url: string): string {
    return `${this.baseUrl}/${url}`;
  }
}

export default RestApiService;
